/**
 * Обещание, с которым человек уходит на страницу конторы.
 *
 * Мы показываем кэф, человек нажимает «вход», открывается чужая вкладка — и с
 * этой секунды наши числа перестают что-либо значить. Между нашей выдачей и его
 * купоном стоят: задержка донора, задержка площадки и его собственные секунды на
 * поиск рынка. Всё, что мы можем честно сделать, — передать вместе с переходом
 * СВОЁ число и время, когда оно было верно, чтобы на той стороне было с чем
 * сверять.
 *
 * Обещание живёт в объекте, а не в URL, по двум причинам. Хвост в адресе чужого
 * сайта иногда ломает маршрутизацию и всегда попадает в их логи — нам не нужно,
 * чтобы контора видела метку нашего сканера на каждом переходе. И размер: в
 * обещании едет исход словами, которым подписана полоса на чужой странице.
 *
 * Составы матча появились здесь 10.08 — ровно тогда, когда появился способ ими
 * пользоваться. Раньше поля не было намеренно: искать блок на чужой странице по
 * имени мы не умели, а поле, которое никто не читает, обещает точность, которой
 * нет. Теперь его читает прицел (`aim/`), и читает не в одиночку: имя называет
 * строку, а решает всё равно совпадение числа. Имена приезжают в написании ТОЙ
 * САМОЙ площадки, куда открылась вкладка, — донор хранит их по площадкам, и
 * сводить «Team Spirit» с «Team Spirit (CS2)» никому не приходится.
 */

import { participantText } from '../lib/participantText';

/** Что мы обещали по одной ноге. */
export interface Promise_ {
  /** Private call execution. It can prepare a coupon, never confirm a bet. */
  prepareOnly?: true;
  executionId?: string;
  expiresAt?: number;
  /** Calls use scanner prices and never wait for an extension odds check. */
  priceCheck?: false;
  /** Наш код площадки. */
  venue: string;
  /** Адрес, который открылся. Без `forq_intent`: контора его не должна видеть. */
  url: string;
  /**
   * Своя вкладка, не хвост в чужом адресе. Старое обещание поля не несёт:
   * тогда метка ещё могла стоять в `url`.
   */
  intent?: string;
  /** Кэф, который человек видел у нас. Строкой: денежные числа не через float. */
  odds: string;
  /** Исход словами, как его назвал источник («Тотал больше 2.5»). */
  side: string;
  /**
   * Участники в написании этой площадки. Пусто — прицел ищет по одному числу.
   *
   * Поле добавлено позже остальных, поэтому необязательное: вкладка сканера
   * живёт неделями, и обещание от вчерашней страницы обязано разбираться
   * сегодняшним расширением без ошибки.
   */
  who?: string[];
  /** Сколько ставить. Нужно только для подписи, сверка идёт по кэфу. */
  stake: number | null;
  /** Exact amount for the venue input. New clients use this decimal string. */
  amount?: string;
  currency?: string;
  /** Когда обещание выдано, мс эпохи. */
  at: number;
  /** Тройка или четвёрка конторы `{eid}~{market}~{odd}[~spec]`. Старое обещание поля не несёт. */
  sel?: string;
  /** Период рынка: `M` / `P4`. */
  period?: string;
  /** Код рынка: WIN / TTL / HCP. Старое обещание поля не несёт. */
  kind?: string;
  /** Линия тотала или форы, без знака (`1.5`). */
  line?: string;
  marketKey?: string;
  sport?: string;
  eventLine?: 'live' | 'prematch';
  eventStart?: number;
  /** Имя стороны этой ноги («BIG Academy»). Пусто — выводим из `who` + `side`. */
  name?: string;
  /** Лимитная цена в целых центах. Старое обещание поля не несёт. */
  cents?: string;
  /** Exact quote-currency price, independent of rounded legacy cents. */
  limitPrice?: string;
  /** Шейры, шаг 0.1. Старое обещание поля не несёт. */
  shares?: string;
  /**
   * Нога Polymarket по рынку: `marketUsd` — доллары покупки, `dead` — цена шара,
   * выше которой вилки нет. Старое расширение их не читает и ставит лимитку по
   * `cents`/`shares`, которые едут рядом.
   */
  order?: 'market';
  marketUsd?: string;
  dead?: string;
  /**
   * false — выбрать исход и не печатать сумму. Старое обещание поля не несёт:
   * отсутствие значит «подставлять».
   */
  fillStake?: false;
}

/** Метка нашего окна в `postMessage`. Расширение слушает только её. */
export const CHANNEL = 'forq:confirm';

/** All feeds share the authenticated gateway. Mirror API processes do not
 * own user sessions, so /v2/v1/promise cannot accept an authenticated write. */
export const PROMISE_PATH = '/v1/promise';

/**
 * Обещание протухает: цена, которой полторы минуты, ничего не подтверждает.
 *
 * Порог тот же, что у предупреждения о залипшей цене в live (`next/still.ts`,
 * 150 с), и это не совпадение: там мы говорим «за такой цены никто не следит»,
 * здесь — «за такое время цена успела уехать». Одно измерение, одна граница.
 */
export const PROMISE_TTL_MS = 150_000;

export function fresh(promise: Promise_, now: number): boolean {
  return now - promise.at < PROMISE_TTL_MS && now >= promise.at;
}

/**
 * Разобрать то, что пришло в окно. `null` — это не наше сообщение.
 *
 * Проверяем каждое поле, а не форму целиком: сообщение приходит от кого угодно
 * (страницу можно открыть в чужом iframe), и подставленное «обещание» с чужим
 * кэфом превратило бы подтверждатель в источник ложной уверенности.
 */
export function parse(data: unknown): Promise_ | null {
  if (typeof data !== 'object' || data === null) return null;
  const body = data as Record<string, unknown>;
  if (body.channel !== CHANNEL) return null;
  const promise = body.promise;
  if (typeof promise !== 'object' || promise === null) return null;
  const one = promise as Record<string, unknown>;
  const venue = one.venue;
  const url = one.url;
  const odds = one.odds;
  const at = one.at;
  if (typeof venue !== 'string' || venue === '') return null;
  if (typeof url !== 'string' || url === '') return null;
  if (typeof odds !== 'string' || !/^\d+(\.\d+)?$/.test(odds)) return null;
  if (typeof at !== 'number' || !Number.isFinite(at)) return null;
  const who = Array.isArray(one.who)
    ? one.who.filter((name): name is string => typeof name === 'string' && name !== '').map(participantText)
    : [];
  return {
    venue,
    url,
    odds,
    who,
    side: typeof one.side === 'string' ? participantText(one.side) : '',
    stake: typeof one.stake === 'number' && Number.isFinite(one.stake) ? one.stake : null,
    at,
    ...extras(one),
  };
}

type Extra = 'sel' | 'period' | 'name' | 'kind' | 'line' | 'cents' | 'limitPrice' | 'shares' | 'amount'
  | 'currency' | 'marketKey' | 'sport' | 'eventLine' | 'eventStart' | 'fillStake' | 'intent' | 'order' | 'marketUsd' | 'dead';

/** Мягко: кривой sel не роняет обещание. */
export function extras(
  one: Record<string, unknown>,
): Pick<Promise_, Extra> {
  const out: Pick<Promise_, Extra> = {};
  if (one.fillStake === false) out.fillStake = false;
  if (typeof one.intent === 'string' && /^[a-zA-Z0-9_-]{16,80}$/.test(one.intent)) out.intent = one.intent;
  if (one.eventLine === 'live' || one.eventLine === 'prematch') out.eventLine = one.eventLine;
  if (typeof one.eventStart === 'number' && Number.isSafeInteger(one.eventStart) && one.eventStart > 0 && one.eventStart < 8640000000000000) out.eventStart = one.eventStart;
  if (typeof one.marketKey === 'string' && one.marketKey.length <= 256
    && /^[A-Z0-9_:.,+\-]+$/i.test(one.marketKey) && !/\s/.test(one.marketKey)) out.marketKey = one.marketKey;
  if (typeof one.sport === 'string' && /^[a-z][a-z0-9_]{0,31}$/.test(one.sport) && !/\s/.test(one.sport)) out.sport = one.sport;
  if (typeof one.amount === 'string' && /^\d{1,12}(\.\d{1,8})?$/.test(one.amount)) out.amount = one.amount;
  if (typeof one.currency === 'string' && /^[A-Z]{3,6}$/.test(one.currency)) out.currency = one.currency;
  if (typeof one.sel === 'string') {
    const parts = one.sel.split('~');
    const positiveEvent = one.venue === 'csgopositive' && /^\d+~positive~$/.test(one.sel);
    if (positiveEvent || ((parts.length === 3 || parts.length === 4) && parts.every((bit) => bit !== ''))) {
      out.sel = one.sel;
    }
  }
  if (typeof one.period === 'string' && one.period !== '') out.period = one.period;
  if (typeof one.name === 'string' && one.name.trim() !== '') out.name = participantText(one.name.trim());
  if (typeof one.kind === 'string' && /^(WIN|TTL|HCP|ITTL)$/i.test(one.kind)) {
    out.kind = one.kind.toUpperCase();
  }
  if (typeof one.line === 'string' && one.line === one.line.trim()
    && /^[+-]?\d+(\.\d+)?$/.test(one.line)) out.line = one.line;
  if (typeof one.cents === 'string' && /^\d+$/.test(one.cents) && one.cents !== '0') {
    out.cents = one.cents;
  }
  if (typeof one.limitPrice === 'string' && /^0\.\d{1,6}$/.test(one.limitPrice)
    && !/\s/.test(one.limitPrice) && /[1-9]/.test(one.limitPrice)) out.limitPrice = one.limitPrice;
  if (typeof one.shares === 'string' && /^\d+(\.\d{1,4})?$/.test(one.shares) && one.shares !== '0') {
    out.shares = one.shares;
  }
  Object.assign(out, marketExtras(one));
  return out;
}

/**
 * Рыночная нога: тип и доллары обязательны. Потолок — если уже посчитан;
 * без него первый клик всё равно Market, иначе драйвер ставит лимитку.
 */
export function marketExtras(one: Record<string, unknown>): Pick<Promise_, 'order' | 'marketUsd' | 'dead'> {
  if (one.order !== 'market') return {};
  if (typeof one.marketUsd !== 'string' || !/^\d{1,9}\.\d{2}$/.test(one.marketUsd) || !/[1-9]/.test(one.marketUsd)) return {};
  const out: Pick<Promise_, 'order' | 'marketUsd' | 'dead'> = { order: 'market', marketUsd: one.marketUsd };
  if (typeof one.dead === 'string' && /^0\.\d{1,6}$/.test(one.dead) && /[1-9]/.test(one.dead)) {
    out.dead = one.dead;
  }
  return out;
}

/** Сообщение для окна. Отдельная функция, чтобы форма была ровно одна. */
export function envelope(promise: Promise_): { channel: string; promise: Promise_ } {
  return { channel: CHANNEL, promise };
}
